<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import GroupMembersPicker from 'dashboard/components-next/Contacts/Groups/GroupMembersPicker.vue';
import GroupApiNotice from 'dashboard/components-next/Contacts/Groups/GroupApiNotice.vue';

const props = defineProps({
  contactId: { type: Number, required: true },
});

const MAX_GROUP_MEMBERS = 8;

const { t } = useI18n();
const store = useStore();

const groupGetter = useMapGetter('contactGroups/getGroup');
const uiFlags = useMapGetter('contactGroups/getUIFlags');

const group = computed(() => groupGetter.value(props.contactId));
const members = computed(() => group.value?.members || []);
const memberIds = computed(() => members.value.map(member => member.id));
const inviteLink = computed(
  () =>
    group.value?.metadata?.invite_link ||
    group.value?.metadata?.invite_token ||
    ''
);
const membersCountLabel = computed(() =>
  t('GROUPS.PANEL.MEMBERS_COUNT', {
    count: members.value.length,
    max: MAX_GROUP_MEMBERS,
  })
);
const remainingSlots = computed(() =>
  Math.max(MAX_GROUP_MEMBERS - members.value.length, 0)
);
const canInviteMore = computed(() => remainingSlots.value > 0);

const isOpen = ref(true);
const name = ref('');
const description = ref('');
const newMemberContactIds = ref([]);
const memberMenuId = ref(null);
const memberToRemove = ref(null);
const showRemoveModal = ref(false);
const showRevokeModal = ref(false);
const showDisbandModal = ref(false);
const addMembersDialogRef = ref(null);

const syncForm = groupPayload => {
  if (!groupPayload) return;
  name.value = groupPayload.name || '';
  description.value = groupPayload.metadata?.description || '';
};

watch(group, syncForm, { immediate: true });

watch(
  () => props.contactId,
  contactId => {
    if (contactId) {
      store.dispatch('contactGroups/show', { contactId, force: true });
    }
  },
  { immediate: true }
);

const isMetadataDirty = computed(
  () =>
    group.value &&
    (name.value !== (group.value.name || '') ||
      description.value !== (group.value.metadata?.description || ''))
);

const saveMetadata = async () => {
  try {
    await store.dispatch('contactGroups/updateMetadata', {
      contactId: props.contactId,
      name: name.value.trim(),
      description: description.value.trim(),
    });
    useAlert(t('GROUPS.METADATA.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GROUPS.METADATA.ERROR'));
  }
};

const memberMenuItems = () => [
  {
    label: t('GROUPS.PANEL.REMOVE'),
    action: 'remove',
    value: 'remove',
    icon: 'i-lucide-user-minus',
  },
];

const onMemberAction = ({ action }, member) => {
  memberMenuId.value = null;
  if (action === 'remove') {
    memberToRemove.value = member;
    showRemoveModal.value = true;
  }
};

const closeRemoveModal = () => {
  showRemoveModal.value = false;
  memberToRemove.value = null;
};

const confirmMemberRemoval = async () => {
  const member = memberToRemove.value;
  closeRemoveModal();
  if (!member) return;

  try {
    await store.dispatch('contactGroups/removeMember', {
      contactId: props.contactId,
      memberId: member.id,
    });
    useAlert(t('GROUPS.PANEL.REMOVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GROUPS.PANEL.REMOVE_ERROR'));
  }
};

const openAddMembers = () => {
  newMemberContactIds.value = [];
  addMembersDialogRef.value?.open();
};

const addMembers = async () => {
  if (
    !newMemberContactIds.value.length ||
    newMemberContactIds.value.length > remainingSlots.value
  ) {
    return;
  }

  try {
    await store.dispatch('contactGroups/addMembers', {
      contactId: props.contactId,
      contactIds: newMemberContactIds.value,
    });
    newMemberContactIds.value = [];
    addMembersDialogRef.value?.close();
    useAlert(t('GROUPS.PANEL.ADD_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GROUPS.PANEL.ADD_ERROR'));
  }
};

const generateInvite = async () => {
  try {
    await store.dispatch('contactGroups/createInvite', {
      contactId: props.contactId,
    });
    useAlert(t('GROUPS.INVITE.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GROUPS.INVITE.ERROR'));
  }
};

const copyInvite = async () => {
  try {
    await copyTextToClipboard(inviteLink.value);
    useAlert(t('GROUPS.INVITE.COPIED'));
  } catch {
    useAlert(t('GROUPS.INVITE.COPY_ERROR'));
  }
};

const confirmInviteRevocation = async () => {
  showRevokeModal.value = false;
  try {
    await store.dispatch('contactGroups/revokeInvite', {
      contactId: props.contactId,
    });
    useAlert(t('GROUPS.INVITE.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GROUPS.INVITE.ERROR'));
  }
};

const confirmDisband = async () => {
  showDisbandModal.value = false;
  try {
    await store.dispatch('contactGroups/disband', {
      contactId: props.contactId,
    });
    useAlert(t('GROUPS.DISBAND.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('GROUPS.DISBAND.ERROR'));
  }
};
</script>

<template>
  <AccordionItem
    :title="t('GROUPS.PANEL.TITLE')"
    :is-open="isOpen"
    @toggle="isOpen = !isOpen"
  >
    <div v-if="group" class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <Avatar :src="group.avatar_url" :name="group.name" :size="40" />
        <div class="flex flex-col min-w-0">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ group.name }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ membersCountLabel }}
          </span>
        </div>
      </div>

      <GroupApiNotice compact />

      <div class="flex flex-col gap-3">
        <Input
          v-model="name"
          :label="t('GROUPS.METADATA.NAME_LABEL')"
          :disabled="uiFlags.isUpdating"
        />
        <TextArea
          v-model="description"
          :label="t('GROUPS.METADATA.DESCRIPTION_LABEL')"
          :placeholder="t('GROUPS.METADATA.DESCRIPTION_PLACEHOLDER')"
          :disabled="uiFlags.isUpdating"
          auto-height
        />
        <Button
          v-if="isMetadataDirty"
          xs
          :label="t('GROUPS.METADATA.SAVE')"
          :is-loading="uiFlags.isUpdating"
          @click="saveMetadata"
        />
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GROUPS.PANEL.MEMBERS') }}
          </span>
          <Button
            xs
            ghost
            icon="i-lucide-link"
            :label="t('GROUPS.PANEL.INVITE')"
            :disabled="!canInviteMore"
            @click="openAddMembers"
          />
        </div>
        <p v-if="!members.length" class="mb-0 text-xs text-n-slate-11">
          {{ t('GROUPS.PANEL.EMPTY') }}
        </p>
        <ul v-else class="flex flex-col gap-1 mb-0">
          <li
            v-for="member in members"
            :key="member.id"
            class="flex items-center gap-2"
          >
            <Avatar :name="member.name" :size="24" />
            <span class="flex-1 min-w-0 text-sm truncate text-n-slate-12">
              {{ member.name }}
            </span>
            <span
              v-if="member.admin"
              class="text-xs rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-n-slate-11"
            >
              {{ t('GROUPS.PANEL.ADMIN') }}
            </span>
            <div class="relative flex-shrink-0">
              <Button
                icon="i-lucide-ellipsis-vertical"
                color="slate"
                variant="ghost"
                xs
                @click="memberMenuId = member.id"
              />
              <DropdownMenu
                v-if="memberMenuId === member.id"
                v-on-clickaway="() => (memberMenuId = null)"
                :menu-items="memberMenuItems(member)"
                class="ltr:right-0 rtl:left-0 mt-1 w-52 top-full"
                @action="onMemberAction($event, member)"
              />
            </div>
          </li>
        </ul>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('GROUPS.INVITE.TITLE') }}
        </span>
        <p v-if="!inviteLink" class="mb-0 text-xs text-n-slate-11">
          {{ t('GROUPS.INVITE.EMPTY') }}
        </p>
        <div v-else class="flex items-center gap-1">
          <span
            class="flex-1 min-w-0 text-xs truncate rounded-lg bg-n-alpha-2 px-2 py-1 text-n-slate-12"
          >
            {{ inviteLink }}
          </span>
          <Button
            v-tooltip="t('GROUPS.INVITE.COPY')"
            icon="i-lucide-copy"
            color="slate"
            variant="ghost"
            xs
            @click="copyInvite"
          />
          <Button
            v-tooltip="t('GROUPS.INVITE.REVOKE')"
            icon="i-lucide-link-2-off"
            color="ruby"
            variant="ghost"
            xs
            @click="showRevokeModal = true"
          />
        </div>
        <Button
          v-if="!inviteLink"
          xs
          color="slate"
          icon="i-lucide-link"
          :label="t('GROUPS.INVITE.GENERATE')"
          :is-loading="uiFlags.isUpdating"
          @click="generateInvite"
        />
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          xs
          color="ruby"
          variant="faded"
          :label="t('GROUPS.DISBAND.BUTTON')"
          @click="showDisbandModal = true"
        />
      </div>
    </div>

    <Dialog
      ref="addMembersDialogRef"
      width="md"
      :title="t('GROUPS.PANEL.INVITE_TITLE')"
      :confirm-button-label="t('GROUPS.PANEL.INVITE_CONFIRM')"
      :disable-confirm-button="
        !newMemberContactIds.length ||
        newMemberContactIds.length > remainingSlots
      "
      :is-loading="uiFlags.isUpdating"
      @confirm="addMembers"
      @close="newMemberContactIds = []"
    >
      <p class="mt-0 mb-3 text-xs text-n-slate-11">
        {{ t('GROUPS.PANEL.INVITE_HINT', { count: remainingSlots }) }}
      </p>
      <GroupMembersPicker
        v-model="newMemberContactIds"
        :excluded-contact-ids="memberIds"
        :max="remainingSlots"
        :placeholder="t('GROUPS.PICKER.PLACEHOLDER')"
      />
    </Dialog>

    <woot-delete-modal
      v-model:show="showRemoveModal"
      :on-close="closeRemoveModal"
      :on-confirm="confirmMemberRemoval"
      :title="t('GROUPS.PANEL.REMOVE_TITLE')"
      :message="t('GROUPS.PANEL.REMOVE_MESSAGE')"
      :message-value="memberToRemove ? ` ${memberToRemove.name}?` : ''"
      :confirm-text="t('GROUPS.PANEL.REMOVE_CONFIRM')"
      :reject-text="t('GROUPS.PANEL.CANCEL')"
    />

    <woot-delete-modal
      v-model:show="showRevokeModal"
      :on-close="() => (showRevokeModal = false)"
      :on-confirm="confirmInviteRevocation"
      :title="t('GROUPS.INVITE.REVOKE_TITLE')"
      :message="t('GROUPS.INVITE.REVOKE_MESSAGE')"
      :confirm-text="t('GROUPS.INVITE.REVOKE_CONFIRM')"
      :reject-text="t('GROUPS.PANEL.CANCEL')"
    />

    <woot-delete-modal
      v-model:show="showDisbandModal"
      :on-close="() => (showDisbandModal = false)"
      :on-confirm="confirmDisband"
      :title="t('GROUPS.DISBAND.TITLE')"
      :message="t('GROUPS.DISBAND.MESSAGE')"
      :confirm-text="t('GROUPS.DISBAND.CONFIRM')"
      :reject-text="t('GROUPS.PANEL.CANCEL')"
    />
  </AccordionItem>
</template>
